import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Input, Modal, Popconfirm, Row, Select, Space, Table } from "antd";
import { FIRESTORE_CATEGORIES_TABLE } from "../../../constants/FirestoreConstant";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useFirestore } from "react-redux-firebase";
import { formatNameToURL } from "../../../functions/formatters";
import { HolderOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { v4 as uuidv4 } from "uuid";
import * as dayjs from "dayjs";

const RowContext = React.createContext({});

const DragHandle = () => {
  const {setActivatorNodeRef, listeners} = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined/>}
      style={{cursor: 'move'}}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const DraggableRow = props => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({id: props['data-row-key']});
  const style = Object.assign(
    Object.assign(Object.assign({}, props.style), {
      transform: CSS.Translate.toString(transform),
      transition
    }),
    isDragging ? {position: 'relative', zIndex: 9999} : {}
  );
  const contextValue = useMemo(
    () => ({setActivatorNodeRef, listeners}),
    [setActivatorNodeRef, listeners]
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

export default function Categories() {
  const {t} = useTranslation();
  const categories = useSelector((state) => state.firestoreReducer.ordered.categories);
  const firestore = useFirestore();
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [list, setList] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveSortingOrderDisabled, setSaveSortingOrderDisabled] = useState(true);

  useEffect(() => {
    setList(categories.map((category, index) => ({...category, key: category.position})));
  }, [categories]);

  const deleteCategory = async (category) => {
    // Delete product from Firestore
    firestore
      .collection(FIRESTORE_CATEGORIES_TABLE)
      .doc(category.id)
      .delete()
      .then(async () => {
        // Remove from list
        const filteredList = list.filter((item) => item.id !== category.id);
        // Assign new keys based on new index
        const updatedList = filteredList.map((item, index) => ({...item, key: index}));
        // Save reordering to Firestore
        const batch = firestore.batch();
        updatedList.forEach(item => {
          const docRef = firestore.collection(FIRESTORE_CATEGORIES_TABLE).doc(item.id);
          batch.update(docRef, { position: item.key });
        });
        await batch.commit();
        setList(updatedList);
      });
  };

  const handleCreateCategory = () => {
    addForm.validateFields().then(async (values) => {
      values.categoryId = uuidv4();
      values.synonyms = !!values.synonyms ? formatSynonyms(values.synonyms) : [];
      values.subcategories = !!values.subcategories ? values.subcategories : [];
      values.subcategories = values.subcategories.map((subcategory) => ({
        ...subcategory,
        synonyms: formatSynonyms(subcategory.synonyms),
        subcategoryId: uuidv4()
      }));
      values.subcategoryIds = !!values.subcategories
        ? values.subcategories.map((subcategory) => subcategory.subcategoryId)
        : [];
      values.subcategorySlugs = !!values.subcategories
        ? values.subcategories.map((subcategory) => subcategory.slug)
        : [];
      values.createdAt = dayjs().unix();
      values.position = list.length; // Adds a sorting position, placing it at the end
      await firestore.collection(FIRESTORE_CATEGORIES_TABLE).add(values);
      closeAddModal();
    });
  };

  const handleUpdateCategory = () => {
    editForm.validateFields().then(async (values) => {
      values.synonyms = !!values.synonyms ? formatSynonyms(values.synonyms) : [];
      values.subcategories = !!values.subcategories ? values.subcategories : [];
      values.subcategories = values.subcategories.map((subcategory) =>
        subcategory.subcategoryId
          ? {...subcategory, synonyms: formatSynonyms(subcategory.synonyms)}
          : {
            ...subcategory,
            synonyms: formatSynonyms(subcategory.synonyms),
            subcategoryId: uuidv4()
          }
      );
      values.subcategoryIds = !!values.subcategories
        ? values.subcategories.map((subcategory) => subcategory.subcategoryId)
        : [];
      values.subcategorySlugs = !!values.subcategories
        ? values.subcategories.map((subcategory) => subcategory.slug)
        : [];
      values.updatedAt = dayjs().unix();
      await firestore.collection(FIRESTORE_CATEGORIES_TABLE).doc(currentCategory.id).update(values);
      closeEditModal();
    });
  };

  const saveSortingOrder = async () => {
    setLoading(true);
    const batch = firestore.batch();
    // Assign new keys based on index
    const updatedList = list.map((item, index) => ({...item, key: index}));
    // Add all updates to the batch
    updatedList.forEach(item => {
      const docRef = firestore.collection(FIRESTORE_CATEGORIES_TABLE).doc(item.id);
      batch.update(docRef, { position: item.key });
    });

    // Commit the entire batch at once
    await batch.commit();
    setList(updatedList);
    setLoading(false);
    setSaveSortingOrderDisabled(true);
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const openEditModal = (category) => {
    setCurrentCategory(category);
    editForm.setFieldsValue(category);
    setEditModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    addForm.resetFields();
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setCurrentCategory(null);
    editForm.resetFields();
  };

  const formatCategoryUrl = (productName, field) => {
    let formattedName = formatNameToURL(productName);
    addForm.setFields([{name: field, value: formattedName}]);
  };

  const formatSynonyms = (items) => {
    if (!items) return [];
    const lowerCaseItems = [];
    items.forEach((item) => lowerCaseItems.push(item.toLowerCase()));
    return lowerCaseItems;
  };

  const onDragEnd = ({active, over}) => {
    if (active.id !== (over === null || over === void 0 ? void 0 : over.id)) {
      setSaveSortingOrderDisabled(false);
      setList(prevState => {
        const activeIndex = prevState.findIndex(
          record => record.key === (active === null || active === void 0 ? void 0 : active.id)
        );
        const overIndex = prevState.findIndex(
          record => record.key === (over === null || over === void 0 ? void 0 : over.id)
        );
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };

  return (
    <>
      <h2>{t("categories")}</h2>
      <Card>
        <Space className={"mb-2 d-flex justify-content-between"}>
          <Input placeholder={t("search")}/>
          <div style={{display: "flex", gap: 8}}>
            <Button disabled={saveSortingOrderDisabled} onClick={saveSortingOrder}>{t("save_sorting_order")}</Button>
            <Button type={"primary"} onClick={openAddModal}>
              {t("add")}
            </Button>
          </div>
        </Space>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext items={list.map(i => i.key)} strategy={verticalListSortingStrategy}>
            <Table
              loading={loading}
              rowKey="key"
              components={{body: {row: DraggableRow}}}
              columns={[
                {key: 'sort', align: 'center', width: 80, render: () => <DragHandle/>},
                {
                  title: t("category"),
                  dataIndex: "name",
                  render: (_, category) => <a onClick={() => openEditModal(category)}>{category.name}</a>
                },
                {
                  title: t("subcategories"),
                  dataIndex: "subcategories",
                  render: (data) => (
                    <div>
                      {data.map((item, index) => (
                        <div key={index}>{item.name}</div>
                      ))}
                    </div>
                  )
                },
                {
                  title: t("action"),
                  key: "action",
                  render: (_, category) => (
                    <Space size="middle">
                      <Popconfirm
                        title={t("delete_item", {name: category.name})}
                        onConfirm={() => deleteCategory(category)}
                        okText={t("yes")}
                        cancelText={t("no")}
                      >
                        <a>{t("delete")}</a>
                      </Popconfirm>
                    </Space>
                  )
                }
              ]}
              dataSource={list}
              scroll={{x: true}}
            />
          </SortableContext>
        </DndContext>
      </Card>
      <Modal
        open={addModalOpen}
        title={t("add_category")}
        onCancel={closeAddModal}
        onOk={handleCreateCategory}
        okText={t("save")}
        cancelText={t("close")}
        width={1000}
      >
        <Form layout={"vertical"} form={addForm}>
          <h4>{t("category_info")}</h4>
          <Row gutter={16}>
            <Col md={8}>
              <Form.Item
                name={"name"}
                label={t("category_name_nl")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_value")
                  }
                ]}
              >
                <Input placeholder={t("category_name_nl")}/>
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name={"nameEN"}
                label={t("category_name_en")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_value")
                  }
                ]}
              >
                <Input
                  placeholder={t("category_name_en")}
                  onChange={(e) => formatCategoryUrl(e.target.value, "slug")}
                />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name={"nameDE"}
                label={t("category_name_de")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_value")
                  }
                ]}
              >
                <Input placeholder={t("category_name_de")}/>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={"slug"}
                label={t("slug")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_value")
                  }
                ]}
              >
                <Input placeholder={t("slug")}/>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={"synonyms"} label={t("synonyms")}>
                <Select mode="tags" style={{width: "100%"}} placeholder={t("synonyms")} options={[]}/>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <h4>{t("subcategories")}</h4>
              <Form.List name="subcategories">
                {(fields, {add, remove}) => {
                  return (
                    <div>
                      {fields.map((field, index) => (
                        <Row key={field.key} gutter={10}>
                          <Col md={7}>
                            <Form.Item
                              name={[field.name, "name"]}
                              label={t("category_name_nl")}
                              rules={[
                                {
                                  required: true,
                                  message: t("form.enter_value")
                                }
                              ]}
                            >
                              <Input placeholder={t("category_name_nl")}/>
                            </Form.Item>
                          </Col>
                          <Col md={7}>
                            <Form.Item
                              name={[field.name, "nameEN"]}
                              label={t("category_name_en")}
                              rules={[
                                {
                                  required: true,
                                  message: t("form.enter_value")
                                }
                              ]}
                            >
                              <Input
                                placeholder={t("category_name_en")}
                                onChange={(e) => formatCategoryUrl(e.target.value, ["subcategories", index, "slug"])}
                              />
                            </Form.Item>
                          </Col>
                          <Col md={7}>
                            <Form.Item
                              name={[field.name, "nameDE"]}
                              label={t("category_name_de")}
                              rules={[
                                {
                                  required: true,
                                  message: t("form.enter_value")
                                }
                              ]}
                            >
                              <Input placeholder={t("category_name_de")}/>
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={3} style={{alignSelf: "center"}}>
                            <MinusCircleOutlined
                              onClick={() => {
                                remove(field.name);
                              }}
                            />
                          </Col>{" "}
                          <Col xs={24} md={12}>
                            <Form.Item
                              {...field}
                              name={[field.name, "slug"]}
                              label={t("slug")}
                              rules={[
                                {
                                  required: true,
                                  message: t("form.enter_value")
                                }
                              ]}
                            >
                              <Input placeholder={t("slug")}/>
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item {...field} name={[field.name, "synonyms"]} label={t("synonyms")}>
                              <Select mode="tags" style={{width: "100%"}} placeholder={t("synonyms")} options={[]}/>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <hr className="mt-2"/>
                          </Col>
                        </Row>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => {
                            add();
                          }}
                          className="w-100"
                        >
                          <PlusOutlined/> {t("form.add_field")}
                        </Button>
                      </Form.Item>
                    </div>
                  );
                }}
              </Form.List>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        open={editModalOpen}
        title={t("edit_item", {name: currentCategory?.name})}
        onCancel={closeEditModal}
        onOk={handleUpdateCategory}
        okText={t("save")}
        cancelText={t("close")}
        width={1000}
      >
        <Form layout={"vertical"} form={editForm}>
          <h4>{t("category_info")}</h4>
          <Row gutter={16}>
            <Col md={8}>
              <Form.Item
                name={"name"}
                label={t("category_name_nl")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_value")
                  }
                ]}
              >
                <Input placeholder={t("category_name_nl")}/>
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name={"nameEN"}
                label={t("category_name_en")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_value")
                  }
                ]}
              >
                <Input
                  placeholder={t("category_name_en")}
                  onChange={(e) => formatCategoryUrl(e.target.value, "slug")}
                />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name={"nameDE"}
                label={t("category_name_de")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_value")
                  }
                ]}
              >
                <Input placeholder={t("category_name_de")}/>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={"slug"}
                label={t("slug")}
                rules={[
                  {
                    required: true,
                    message: t("form.enter_value")
                  }
                ]}
              >
                <Input placeholder={t("slug")}/>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={"synonyms"} label={t("synonyms")}>
                <Select mode="tags" style={{width: "100%"}} placeholder={t("synonyms")} options={[]}/>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <h4>{t("subcategories")}</h4>
              <Form.List name="subcategories">
                {(fields, {add, remove}) => {
                  return (
                    <div>
                      {fields.map((field, index) => (
                        <Row key={field.key} gutter={10}>
                          <Col md={7}>
                            <Form.Item
                              name={[field.name, "name"]}
                              label={t("category_name_nl")}
                              rules={[
                                {
                                  required: true,
                                  message: t("form.enter_value")
                                }
                              ]}
                            >
                              <Input placeholder={t("category_name_nl")}/>
                            </Form.Item>
                          </Col>
                          <Col md={7}>
                            <Form.Item
                              name={[field.name, "nameEN"]}
                              label={t("category_name_en")}
                              rules={[
                                {
                                  required: true,
                                  message: t("form.enter_value")
                                }
                              ]}
                            >
                              <Input
                                placeholder={t("category_name_en")}
                                onChange={(e) => formatCategoryUrl(e.target.value, ["subcategories", index, "slug"])}
                              />
                            </Form.Item>
                          </Col>
                          <Col md={7}>
                            <Form.Item
                              name={[field.name, "nameDE"]}
                              label={t("category_name_de")}
                              rules={[
                                {
                                  required: true,
                                  message: t("form.enter_value")
                                }
                              ]}
                            >
                              <Input placeholder={t("category_name_de")}/>
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={3} style={{alignSelf: "center"}}>
                            <MinusCircleOutlined
                              onClick={() => {
                                remove(field.name);
                              }}
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              {...field}
                              name={[field.name, "slug"]}
                              label={t("slug")}
                              rules={[
                                {
                                  required: true,
                                  message: t("form.enter_value")
                                }
                              ]}
                            >
                              <Input placeholder={t("slug")}/>
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item {...field} name={[field.name, "synonyms"]} label={t("synonyms")}>
                              <Select mode="tags" style={{width: "100%"}} placeholder={t("synonyms")} options={[]}/>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <hr className="mt-2"/>
                          </Col>
                        </Row>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => {
                            add();
                          }}
                          className="w-100"
                        >
                          <PlusOutlined/> {t("form.add_field")}
                        </Button>
                      </Form.Item>
                    </div>
                  );
                }}
              </Form.List>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
